import { useState, useEffect } from "react";
import { PersonalInfo } from "@/types/portfolio";
import { SiteConfig } from "@/types/siteconfig";
import { supabase } from "@/integrations/supabase/client";
import * as LucideIcons from "lucide-react";

interface AboutProps {
  info: PersonalInfo;
}

interface AboutHighlight {
  id: string;
  text: string;
  icon: string;
  order: number;
}

export const About = ({ info }: AboutProps) => {
  const [highlights, setHighlights] = useState<AboutHighlight[]>([]);
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);

  useEffect(() => {
    loadHighlights();
    loadSiteConfig();
  }, []);

  const loadHighlights = async () => {
    try {
      const { data, error } = await supabase
        .from('about_highlights')
        .select('*')
        .order('order', { ascending: true });

      if (error) throw error;
      setHighlights(data || []);
    } catch (error) {
      console.error('Error loading highlights:', error);
    }
  };

  const loadSiteConfig = async () => {
    try {
      const { data } = await supabase
        .from('site_config')
        .select('*')
        .limit(1)
        .maybeSingle();
      
      setSiteConfig(data);
    } catch (error) {
      console.error('Error loading site config:', error);
    }
  };

  const shouldShow = (field: string) => {
    return info.field_visibility?.[field]?.homepage !== false;
  };

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName] || LucideIcons.CheckCircle2;
    return Icon;
  };

  return (
    <section id="about" className="py-24 px-4 bg-muted/30 relative">
      {/* Subtle top gradient */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      
      <div className="max-w-4xl mx-auto space-y-12">
        <header className="text-center space-y-4">
          <h2 className="text-3xl md:text-4xl font-bold">
            {siteConfig?.about_title || 'Over mij'}
          </h2>
          {(siteConfig?.about_subtitle || shouldShow('bio')) && (
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {siteConfig?.about_subtitle || info.bio}
            </p>
          )}
        </header>

        {highlights.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4 pt-4">
            {highlights.map((highlight, index) => {
              const Icon = getIcon(highlight.icon);
              return (
                <div
                  key={highlight.id}
                  className="group flex items-start gap-4 p-5 rounded-xl bg-card border border-border/50 hover:border-border hover:shadow-md transition-all duration-200"
                  style={{ 
                    animationDelay: `${index * 100}ms`,
                    opacity: 0,
                    animation: 'fade-in 0.5s ease-out forwards'
                  }}
                >
                  <div className="icon-container flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <p className="text-sm md:text-base text-foreground/90 leading-relaxed">{highlight.text}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Subtle bottom gradient */}
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
    </section>
  );
};